// Shared helper: refresh cart drawer using the ?section_id= endpoint.
// Works in both shopify theme dev (127.0.0.1) and production.
async function _refreshCartDrawer(drawer) {
  const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart';
  const themeParam = window.Shopify?.theme?.id ? `&preview_theme_id=${window.Shopify.theme.id}` : '';
  const [drawerHtml, iconHtml] = await Promise.all([
    fetch(`${cartUrl}?section_id=cart-drawer${themeParam}`).then(r => r.text()).catch(() => null),
    fetch(`${cartUrl}?section_id=cart-icon-bubble${themeParam}`).then(r => r.text()).catch(() => null),
  ]);
  if (drawerHtml) {
    const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
    const newCartDrawer = doc.querySelector('#CartDrawer');
    const liveCartDrawer = document.querySelector('#CartDrawer');
    if (newCartDrawer && liveCartDrawer) {
      liveCartDrawer.innerHTML = newCartDrawer.innerHTML;
    }
    // .drawer__inner-empty is present in section HTML only when cart == empty
    drawer.classList.toggle('is-empty', !!doc.querySelector('.drawer__inner-empty'));
  }
  if (iconHtml) {
    const iconEl = document.getElementById('cart-icon-bubble');
    const src = new DOMParser().parseFromString(iconHtml, 'text/html').querySelector('.shopify-section');
    if (iconEl && src) iconEl.innerHTML = src.innerHTML;
  }
  // Re-attach overlay listener since innerHTML was replaced
  const overlay = document.getElementById('CartDrawer-Overlay');
  if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
  drawer.open();
}

class BundleSet {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    const dataEl = document.getElementById(`BundleData-${this.sectionId}`);
    if (!dataEl) { console.error(`[BundleSet] BundleData-${this.sectionId} not found`); return; }

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[BundleSet] Failed to parse data island JSON:', e, '\nRaw:', dataEl.textContent);
      return;
    }

    console.log('[BundleSet] init', this.sectionId, this.data);

    const def = this.data.defaults;
    this.mode = 'match';
    this.sel = { matchColor: def.matchColor, shirtColor: def.mixShirtColor, trouserColor: def.mixTrouserColor, shirtSize: null, trouserSize: null };
    this.currentImageKey = `${def.matchColor}-${def.matchColor}`; // matches server-rendered image, avoids reload on init
    this.image = document.getElementById(`BundleImage-${this.sectionId}`);
    this.cta    = document.getElementById(`BundleCta-${this.sectionId}`);
    this.buyNow = document.getElementById(`BundleBuyNow-${this.sectionId}`);
    this.error  = document.getElementById(`BundleError-${this.sectionId}`);

    if (!this.cta) { console.error(`[BundleSet] BundleCta-${this.sectionId} not found`); return; }

    this._bindToggle();
    this._bindSwatches();
    this._bindSizes();
    this.cta.addEventListener('click', () => this._addToCart({ checkout: false }));
    this.buyNow?.addEventListener('click', () => this._addToCart({ checkout: true }));

    // Pre-select default size on load
    this._updateSizes('shirt', def.matchColor);
    this._updateSizes('trouser', def.matchColor);
  }

  _bindToggle() {
    this.container.querySelectorAll('.bundle-set__toggle-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.dataset.mode === this.mode) return;
        this.mode = btn.dataset.mode;
        this.container.querySelectorAll('.bundle-set__toggle-btn').forEach((b) =>
          b.classList.toggle('is-active', b.dataset.mode === this.mode)
        );
        this.container.querySelector('[data-color-context="match"]').classList.toggle('is-hidden', this.mode !== 'match');
        this.container.querySelector('[data-color-context="shirt"]').classList.toggle('is-hidden', this.mode !== 'mix');
        this.container.querySelector('[data-color-context="trouser"]').classList.toggle('is-hidden', this.mode !== 'mix');
        if (this.mode === 'mix') {
          this._setSwatchSelected('shirt', this.sel.matchColor);
          this.sel.shirtColor = this.sel.matchColor;
          this._updateSizes('shirt', this.sel.shirtColor);
          this._setSwatchSelected('trouser', this.data.defaults.mixTrouserColor);
          this.sel.trouserColor = this.data.defaults.mixTrouserColor;
          this._updateSizes('trouser', this.data.defaults.mixTrouserColor);
        }
        this._updateImage();
      });
    });
  }

  _bindSwatches() {
    this.container.querySelectorAll('.bundle-set__swatch').forEach((sw) => {
      sw.addEventListener('click', () => {
        const { color, context } = sw.dataset;
        this._setSwatchSelected(context, color);
        if (context === 'match') {
          this.sel.matchColor = color;
          this._updateSizes('shirt', color);
          this._updateSizes('trouser', color);
        } else if (context === 'shirt') {
          this.sel.shirtColor = color;
          this._updateSizes('shirt', color);
        } else {
          this.sel.trouserColor = color;
          this._updateSizes('trouser', color);
        }
        this._updateImage();
      });
    });
  }

  _setSwatchSelected(context, color) {
    this.container.querySelectorAll(`.bundle-set__swatch[data-context="${context}"]`).forEach((s) => {
      const active = s.dataset.color === color;
      s.classList.toggle('is-selected', active);
      s.setAttribute('aria-pressed', String(active));
    });
  }

  _updateSizes(item, color) {
    const map = this.data.variants[`${item}s`]?.[color];
    if (item === 'shirt') this.sel.shirtSize = null;
    else this.sel.trouserSize = null;
    this.container.querySelectorAll(`.bundle-set__size[data-item="${item}"]`).forEach((btn) => {
      // if product not configured in customizer, keep sizes as rendered (don't disable)
      if (!map) { btn.classList.remove('is-selected'); return; }
      const info = map[btn.dataset.size];
      btn.disabled = !info?.available;
      btn.classList.toggle('is-unavailable', !info?.available);
      btn.classList.remove('is-selected');
    });
    this._selectDefaultSize(item);
  }

  _selectDefaultSize(item) {
    const size = this.data.defaults.size;
    const btn = this.container.querySelector(`.bundle-set__size[data-item="${item}"][data-size="${size}"]`);
    if (!btn || btn.disabled) return;
    btn.classList.add('is-selected');
    if (item === 'shirt') this.sel.shirtSize = size;
    else this.sel.trouserSize = size;
  }

  _bindSizes() {
    this.container.querySelectorAll('.bundle-set__size').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const { item, size } = btn.dataset;
        this.container.querySelectorAll(`.bundle-set__size[data-item="${item}"]`).forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        if (item === 'shirt') this.sel.shirtSize = size;
        else this.sel.trouserSize = size;
      });
    });
  }

  _imageKey() {
    const sc = this.mode === 'match' ? this.sel.matchColor : this.sel.shirtColor;
    const tc = this.mode === 'match' ? this.sel.matchColor : this.sel.trouserColor;
    return `${sc}-${tc}`;
  }

  _updateImage() {
    if (!this.image) return;
    const key = this._imageKey();
    const url = this.data.images[key] || this.data.fallbackImage;
    if (key === this.currentImageKey) return;
    if (!url) return;
    this.currentImageKey = key;
    this.image.classList.add('is-loading');
    const tmp = new Image();
    tmp.onload = () => {
      this.image.srcset = '';  // clear srcset or browser ignores src change
      this.image.src = url;
      this.image.classList.remove('is-loading');
    };
    tmp.onerror = () => { this.image.classList.remove('is-loading'); };
    tmp.src = url;
  }

  _shirtColor()   { return this.mode === 'match' ? this.sel.matchColor : this.sel.shirtColor; }
  _trouserColor() { return this.mode === 'match' ? this.sel.matchColor : this.sel.trouserColor; }

  _validate() {
    if (!this.sel.shirtSize)   return 'Please select a shirt size.';
    if (!this.sel.trouserSize) return 'Please select a trouser size.';
    if (!this.data.bundleVariantId) return 'Bundle product not configured — assign it in the customizer.';
    const sv = this.data.variants.shirts[this._shirtColor()]?.[this.sel.shirtSize];
    const tv = this.data.variants.trousers[this._trouserColor()]?.[this.sel.trouserSize];
    if (!sv?.available) return 'Selected shirt size is out of stock.';
    if (!tv?.available) return 'Selected trouser size is out of stock.';
    return null;
  }

  async _addToCart({ checkout = false } = {}) {
    this.error.hidden = true;
    const err = this._validate();
    if (err) { this.error.textContent = err; this.error.hidden = false; return; }

    const sc = this._shirtColor(), tc = this._trouserColor();
    const shirtId   = this.data.variants.shirts[sc][this.sel.shirtSize].id;
    const trouserId = this.data.variants.trousers[tc][this.sel.trouserSize].id;
    const bundleImage = this.data.images[this._imageKey()] || this.data.fallbackImage || this.image?.src || '';

    const activeBtn = checkout ? this.buyNow : this.cta;
    const originalText = activeBtn?.textContent;
    activeBtn?.setAttribute('aria-busy', 'true');
    if (activeBtn) activeBtn.textContent = checkout ? 'Checking out...' : 'Adding...';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: this.data.bundleVariantId, quantity: 1,
          properties: {
            'Shirt Color': sc, 'Shirt Size': this.sel.shirtSize,
            'Trouser Color': tc, 'Trouser Size': this.sel.trouserSize,
            '_shirt_variant_id': String(shirtId),
            '_trouser_variant_id': String(trouserId),
            '_bundle_id': crypto.randomUUID(),
            '_bundle_image': bundleImage,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Could not add to cart.');
      if (checkout) { window.location.href = '/checkout'; return; }
      await this._refreshDrawer(data.id);
    } catch (e) {
      this.error.textContent = e.message; this.error.hidden = false;
    } finally {
      activeBtn?.removeAttribute('aria-busy');
      if (activeBtn && originalText) activeBtn.textContent = originalText;
    }
  }

  async _refreshDrawer(productId) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) { window.location.href = '/cart'; return; }
    await _refreshCartDrawer(drawer);
  }
}

// ── Cross-sell on product pages ──
class BundleCrosssell {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    const dataEl = document.getElementById(`CrosssellData-${this.sectionId}`);
    if (!dataEl) { console.error(`[BundleCrosssell] CrosssellData-${this.sectionId} not found`); return; }

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[BundleCrosssell] Failed to parse data island JSON:', e);
      return;
    }

    this.sel = { currentSize: null, complementColor: this.data.productColor || 'Beige', complementSize: null };
    this.thumb = document.getElementById(`CrosssellThumb-${this.sectionId}`);
    this.sizesContainer = document.getElementById(`CrosssellSizes-${this.sectionId}`);
    this.cta   = document.getElementById(`CrosssellCta-${this.sectionId}`);
    this.error = document.getElementById(`CrosssellError-${this.sectionId}`);

    if (!this.cta) return;
    this._bindCurrentSizes();
    this._bindComplementSwatches();
    this._bindComplementSizes();
    this.cta.addEventListener('click', () => this._addToCart());
  }

  _bindCurrentSizes() {
    this.container.querySelectorAll(`.bundle-set__size[data-item="${this.data.productCategory}"]`).forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        this.container.querySelectorAll(`.bundle-set__size[data-item="${this.data.productCategory}"]`).forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.sel.currentSize = btn.dataset.size;
      });
    });
  }

  _bindComplementSwatches() {
    this.container.querySelectorAll('.bundle-set__swatch[data-context="complement"]').forEach((sw) => {
      sw.addEventListener('click', () => {
        const { color } = sw.dataset;
        this.sel.complementColor = color;
        this.sel.complementSize = null;
        this.container.querySelectorAll('.bundle-set__swatch[data-context="complement"]').forEach((s) => {
          s.classList.toggle('is-selected', s.dataset.color === color);
          s.setAttribute('aria-pressed', String(s.dataset.color === color));
        });
        const prod = this.data.complementProducts[color];
        if (prod?.featuredImage && this.thumb) this.thumb.src = prod.featuredImage;
        this._renderComplementSizes(color);
      });
    });
  }

  _renderComplementSizes(color) {
    const variants = this.data.complementProducts[color]?.variants ?? {};
    this.sizesContainer?.querySelectorAll('.bundle-set__size[data-item="complement"]').forEach((btn) => {
      const info = variants[btn.dataset.size];
      btn.disabled = !info?.available;
      btn.classList.toggle('is-unavailable', !info?.available);
      btn.classList.remove('is-selected');
    });
    this.sel.complementSize = null;
  }

  _bindComplementSizes() {
    this.container.querySelectorAll('.bundle-set__size[data-item="complement"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        this.container.querySelectorAll('.bundle-set__size[data-item="complement"]').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.sel.complementSize = btn.dataset.size;
      });
    });
  }

  async _addToCart() {
    this.error.hidden = true;
    if (!this.sel.currentSize)    { this.error.textContent = `Please select a ${this.data.productCategory} size.`; this.error.hidden = false; return; }
    if (!this.sel.complementSize) { this.error.textContent = "Please select the other item's size."; this.error.hidden = false; return; }

    const isShirt = this.data.productCategory === 'shirt';
    const sc = isShirt ? this.data.productColor : this.sel.complementColor;
    const tc = isShirt ? this.sel.complementColor : this.data.productColor;
    const ss = isShirt ? this.sel.currentSize : this.sel.complementSize;
    const ts = isShirt ? this.sel.complementSize : this.sel.currentSize;
    const shirtId   = isShirt ? this.data.currentVariants[ss]?.id : this.data.complementProducts[sc]?.variants[ss]?.id;
    const trouserId = isShirt ? this.data.complementProducts[tc]?.variants[ts]?.id : this.data.currentVariants[ts]?.id;
    const bundleImage = this.data.bundleProductImage || this.data.complementProducts[this.sel.complementColor]?.featuredImage || '';

    this.cta.setAttribute('aria-busy', 'true');
    this.cta.textContent = 'Adding...';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: this.data.bundleVariantId, quantity: 1,
          properties: {
            'Shirt Color': sc, 'Shirt Size': ss,
            'Trouser Color': tc, 'Trouser Size': ts,
            '_shirt_variant_id': String(shirtId),
            '_trouser_variant_id': String(trouserId),
            '_bundle_id': crypto.randomUUID(),
            '_bundle_image': bundleImage,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Could not add to cart.');
      await this._refreshDrawer(data.id);
    } catch (e) {
      this.error.textContent = e.message; this.error.hidden = false;
    } finally {
      this.cta.removeAttribute('aria-busy'); this.cta.textContent = 'Add Set to Cart';
    }
  }

  async _refreshDrawer(productId) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) { window.location.href = '/cart'; return; }
    await _refreshCartDrawer(drawer);
  }
}

// ── Init ──
document.querySelectorAll('[id^="bundle-set-"]').forEach((el) => {
  try { new BundleSet(el); }
  catch (e) { console.error('[BundleSet] init error:', e); }
});
document.querySelectorAll('.bundle-crosssell[data-section-id]').forEach((el) => {
  try { new BundleCrosssell(el); }
  catch (e) { console.error('[BundleCrosssell] init error:', e); }
});
